import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import { buildMultiListFilterSection } from "../shared/buildListFilterSections";
import PeriodDateSelector from "../../../components/CustomUI/PeriodDateSelector";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import { formatDateTimeTrInAppTz } from "../../../utils/appTime";
import {
  createPeriodState,
  toPeriodISOParams,
  todayPeriodStr,
} from "../../../utils/periodDateUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const durationSortMinutes = (log) => {
  if (log.durationMinutes != null) return Number(log.durationMinutes) || 0;
  if (log.endTime && log.startTime) {
    return Math.round(
      (new Date(log.endTime).getTime() - new Date(log.startTime).getTime()) /
        60000,
    );
  }
  return -1;
};

const tableColumns = [
  {
    name: "Makine",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(a.machine?.name || "").localeCompare(
        String(b.machine?.name || ""),
      ),
  },
  {
    name: "Duruş Nedeni",
    dataType: "text",
    id: 2,
    customSorter: (a, b) =>
      String(a.stopReason?.name || "").localeCompare(
        String(b.stopReason?.name || ""),
      ),
  },
  { name: "OEE Sınıfı / Grubu", id: 3, sortable: false },
  {
    name: "Başlangıç",
    dataType: "text",
    id: 4,
    customSorter: (a, b) =>
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  },
  {
    name: "Bitiş",
    dataType: "text",
    id: 5,
    customSorter: (a, b) => {
      const ta = a.endTime ? new Date(a.endTime).getTime() : 0;
      const tb = b.endTime ? new Date(b.endTime).getTime() : 0;
      return ta - tb;
    },
  },
  {
    name: "Süre (dk)",
    dataType: "number",
    id: 6,
    customSorter: (a, b) => durationSortMinutes(a) - durationSortMinutes(b),
  },
  { name: "Not", id: 7, sortable: false },
  { name: "İşlemler", id: 8, sortable: false },
];

const emptyForm = () => ({
  machine: "",
  stopReason: "",
  startTime: "",
  endTime: "",
  durationMinutes: "",
  notes: "",
});

const fmtDate = (d) => {
  if (!d) return "—";
  return formatDateTimeTrInAppTz(d);
};

const toLocalInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const MachineStopLogs = () => {
  useTitle("Rovion MES - Makine Duruş Kayıtları");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(currentUser.role, "/teep", currentUser.pages || [], !!currentUser.fullAccess);

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("machine-stop-logs-list", tableColumns);

  const [logs, setLogs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [stopReasons, setStopReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });
  const [period, setPeriod] = useState(() =>
    createPeriodState("month", todayPeriodStr()),
  );
  const [machineIds, setMachineIds] = useState([]);
  const [committed, setCommitted] = useState(() => ({
    startDate: createPeriodState("month", todayPeriodStr()).startDate,
    endDate: createPeriodState("month", todayPeriodStr()).endDate,
    machineIds: [],
  }));

  const dismissToast = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken]
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("limit", "200");
      const { startDate, endDate } = toPeriodISOParams(
        committed.startDate,
        committed.endDate,
      );
      p.set("startDate", startDate);
      p.set("endDate", endDate);
      if (committed.machineIds.length) {
        p.set("machineId", committed.machineIds.join(","));
      }
      const { data } = await axios.get(
        `${API_URL}/api/machine-stop-logs?${p}`,
        authHeaders(),
      );
      setLogs(data.stopLogs || []);
    } catch {
      addToast("Kayıtlar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast, committed]);

  const fetchMachines = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/machines`, authHeaders());
      setMachines(data.machines || data || []);
    } catch { }
  }, [authHeaders]);

  const fetchStopReasons = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/stop-reasons`, authHeaders());
      setStopReasons(data.stopReasons || []);
    } catch { }
  }, [authHeaders]);

  useEffect(() => {
    fetchMachines();
    fetchStopReasons();
  }, [fetchMachines, fetchStopReasons]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const applyFilters = useCallback(() => {
    setCommitted({
      startDate: period.startDate,
      endDate: period.endDate,
      machineIds: [...machineIds],
    });
  }, [period.startDate, period.endDate, machineIds]);

  const resetFilters = useCallback(() => {
    const next = createPeriodState("month", todayPeriodStr());
    setPeriod(next);
    setMachineIds([]);
    setCommitted({
      startDate: next.startDate,
      endDate: next.endDate,
      machineIds: [],
    });
  }, []);

  const handlePeriodChange = useCallback((next) => {
    setPeriod(next);
    setCommitted((prev) => ({
      ...prev,
      startDate: next.startDate,
      endDate: next.endDate,
    }));
  }, []);

  const activeFilterCount = machineIds.length;

  const filterSections = useMemo(
    () =>
      machines.length
        ? [
            buildMultiListFilterSection({
              id: "machines",
              title: "Makine",
              selectedIds: machineIds,
              onChange: setMachineIds,
              options: machines.map((m) => ({
                value: String(m._id),
                label: m.name || "—",
                keywords: [m.code, m.name].filter(Boolean),
              })),
              showAllOption: true,
              allLabel: "Tümü",
              searchPlaceholder: "Makine ara…",
              defaultOpen: true,
            }),
          ]
        : [],
    [machines, machineIds],
  );

  const openCreate = () => {
    setFormData(emptyForm());
    setEditingItem(null);
    setSidebarOpened(true);
  };

  const openEdit = (item) => {
    setFormData({
      machine: item.machine?._id || item.machine || "",
      stopReason: item.stopReason?._id || item.stopReason || "",
      startTime: toLocalInput(item.startTime),
      endTime: toLocalInput(item.endTime),
      durationMinutes: item.durationMinutes != null ? String(item.durationMinutes) : "",
      notes: item.notes || "",
    });
    setEditingItem(item);
    setSidebarOpened(true);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingItem(null);
    setFormData(emptyForm());
  };

  const handleSave = async () => {
    if (!formData.machine) { addToast("Makine seçiniz", "danger"); return; }
    if (!formData.startTime) { addToast("Başlangıç zamanı zorunludur", "danger"); return; }
    setSaving(true);
    try {
      const payload = {
        machine: formData.machine,
        stopReason: formData.stopReason || null,
        startTime: formData.startTime ? new Date(formData.startTime).toISOString() : null,
        endTime: formData.endTime ? new Date(formData.endTime).toISOString() : null,
        durationMinutes: formData.durationMinutes !== "" ? Number(formData.durationMinutes) : null,
        notes: formData.notes,
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/machine-stop-logs/${editingItem._id}`, payload, authHeaders());
        addToast("Güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/machine-stop-logs`, payload, authHeaders());
        addToast("Oluşturuldu", "success");
      }
      closeSidebar();
      fetchLogs();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/machine-stop-logs/${id}`, authHeaders());
      addToast("Silindi", "success");
      fetchLogs();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const selectStyle = {
    background: "var(--m-card-background-color, #fff)",
    border: "1px solid #e2e5e8",
    borderRadius: "6px",
  };
  const selectInner = {
    background: "transparent", border: "none", outline: "none",
    width: "100%", padding: "8px 12px", fontSize: "14px",
    color: "inherit", cursor: "pointer",
  };
  const inputStyle = {
    background: "var(--m-card-background-color, #fff)",
    border: "1px solid #e2e5e8",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "14px",
    color: "inherit",
    width: "100%",
    outline: "none",
  };

  return (
    <AppPage
      title="Makine Duruş Kayıtları"
      actions={
        canWrite && (
          <Button appearance="primary-animated" onClick={openCreate} icon={<FiPlus />}>
            Yeni Kayıt
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={t.type === "danger" ? "danger" : "success"} onClose={() => dismissToast(t.id)} />
        ))}
      </ToastContainer>

      <Modal isOpen={confirmModal.isOpen} onClose={() => setConfirmModal({ isOpen: false, id: null })} width="small">
        <ModalHeader>Kaydı Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">Bu duruş kaydını silmek istediğinizden emin misiniz?</p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={() => handleDelete(confirmModal.id)}>Sil</Button>
            <Button appearance="subtle" onClick={() => setConfirmModal({ isOpen: false, id: null })}>İptal</Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          tableId="machine-stop-logs-list"
          loading={loading}
          search={true}
          viewOptions={
            <>
              <PeriodDateSelector
                mode={period.mode}
                anchorDate={period.anchorDate}
                startDate={period.startDate}
                endDate={period.endDate}
                onChange={handlePeriodChange}
                maxDate={todayPeriodStr()}
              />
              <ListViewOptionsPanel
                columns={tableColumns}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                filterSections={filterSections}
                activeFilterCount={activeFilterCount}
                onApply={applyFilters}
                onReset={resetFilters}
              />
            </>
          }
        >
          {logs.map((log) => {
            const sr = log.stopReason;
            const dur = log.durationMinutes != null
              ? log.durationMinutes
              : log.endTime
                ? Math.round((new Date(log.endTime) - new Date(log.startTime)) / 60000)
                : null;
            return (
              <Tr key={log._id} data={log}>
                {visibleColumns.includes(1) && (
                <Td><strong style={{ fontSize: "0.875rem" }}>{log.machine?.name || "—"}</strong></Td>
                )}
                {visibleColumns.includes(2) && (
                <Td>
                  {sr ? (
                    <div className="d-flex align-items-center gap-1">
                      {sr.color && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: sr.color, flexShrink: 0 }} />}
                      <span style={{ fontSize: "0.8rem" }}>{sr.name}</span>
                    </div>
                  ) : <span className="text-muted small">—</span>}
                </Td>
                )}
                {visibleColumns.includes(3) && (
                <Td>
                  {sr?.oeeGroup ? (
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600 }}>{sr.oeeGroup.name}</div>
                      {sr.oeeGroup.oeeClass && (
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: sr.oeeGroup.oeeClass.color || "#6366f1", flexShrink: 0 }} />
                          <span className="text-muted" style={{ fontSize: "0.72rem" }}>{sr.oeeGroup.oeeClass.name}</span>
                        </div>
                      )}
                    </div>
                  ) : <span className="text-muted small">—</span>}
                </Td>
                )}
                {visibleColumns.includes(4) && (
                <Td><span style={{ fontSize: "0.8rem" }}>{fmtDate(log.startTime)}</span></Td>
                )}
                {visibleColumns.includes(5) && (
                <Td><span style={{ fontSize: "0.8rem" }}>{fmtDate(log.endTime)}</span></Td>
                )}
                {visibleColumns.includes(6) && (
                <Td>
                  {dur != null ? (
                    <strong style={{ fontSize: "0.875rem" }}>{dur} dk</strong>
                  ) : <span className="text-muted small">—</span>}
                </Td>
                )}
                {visibleColumns.includes(7) && (
                <Td><span className="text-muted small">{log.notes || "—"}</span></Td>
                )}
                {visibleColumns.includes(8) && (
                <Td>
                  {canWrite && (
                    <div className="d-flex gap-2">
                      <IconButton icon={<FiEdit2 />} label="Düzenle" appearance="subtle" shape="circle" onClick={() => openEdit(log)} />
                      <IconButton icon={<FiTrash2 />} label="Sil" appearance="subtle" shape="circle" onClick={() => setConfirmModal({ isOpen: true, id: log._id })} />
                    </div>
                  )}
                </Td>
                )}
              </Tr>
            );
          })}
        </Table>
      </CardContainer>

      <SidebarContainer show={sidebarOpened} title={editingItem ? "Duruş Kaydını Düzenle" : "Yeni Duruş Kaydı"} onClose={closeSidebar}>
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label required>Makine</Label>
              <div style={selectStyle}>
                <select style={selectInner} value={formData.machine} onChange={(e) => setFormData((p) => ({ ...p, machine: e.target.value }))}>
                  <option value="">— Makine Seçin —</option>
                  {machines.map((m) => (
                    <option key={m._id} value={m._id}>{m.name} ({m.code})</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Duruş Nedeni</Label>
              <div style={selectStyle}>
                <select style={selectInner} value={formData.stopReason} onChange={(e) => setFormData((p) => ({ ...p, stopReason: e.target.value }))}>
                  <option value="">— Neden Seçin —</option>
                  {stopReasons.map((r) => (
                    <option key={r._id} value={r._id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label required>Başlangıç Zamanı</Label>
              <input type="datetime-local" style={inputStyle} value={formData.startTime} onChange={(e) => setFormData((p) => ({ ...p, startTime: e.target.value }))} />
            </div>

            <div>
              <Label>Bitiş Zamanı</Label>
              <input type="datetime-local" style={inputStyle} value={formData.endTime} onChange={(e) => setFormData((p) => ({ ...p, endTime: e.target.value }))} />
            </div>

            <div>
              <Label>Süre (dakika)</Label>
              <Input
                type="number"
                value={formData.durationMinutes}
                onChange={(v) => setFormData((p) => ({ ...p, durationMinutes: v }))}
                placeholder="Bitiş girilmezse manuel süre"
              />
            </div>

            <div>
              <Label>Not</Label>
              <Input value={formData.notes} onChange={(v) => setFormData((p) => ({ ...p, notes: v }))} placeholder="İsteğe bağlı not" />
            </div>
          </div>

          <div className="mt-auto pt-3">
            <Button appearance="primary-animated" onClick={handleSave} disabled={saving} icon={editingItem ? <FiEdit2 /> : <FiPlus />} className="w-100">
              {saving ? "Kaydediliyor..." : editingItem ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(MachineStopLogs);
